import { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, CircularProgress, Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { grnApi, purchaseOrdersApi, branchesApi, type CreateGrnRequest } from "@/api/endpoints";
import { BinHint } from "@/components/BinHint";
import { errorMessage } from "@/api/client";
import { todayIso } from "@/shared/datetime";

interface DraftReceipt {
  purchaseOrderLineId: string;
  itemId: string;
  itemName: string;
  sku: string;
  orderedQty: number;
  alreadyReceived: number;
  pending: number;
  poRate: number;
  // editable
  batchNo: string;
  expiryDate: string;
  receivedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  unitCost: number;
  /** MRP printed on the pack, off the vendor's invoice. 0 = not stated, batch stays unpriced. */
  mrp: number;
  remarks: string;
}

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function CreateGrnPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [poId, setPoId] = useState<string>(() => searchParams.get("poId") ?? "");
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(todayIso);
  const [deliveryNoteRef, setDeliveryNoteRef] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [lines, setLines] = useState<DraftReceipt[]>([]);
  const [postAfterCreate, setPostAfterCreate] = useState(false);

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  // PO list — restrict to Submitted or Approved POs (the ones eligible for GRN)
  const eligiblePoQuery = useQuery({
    queryKey: ["purchase-orders-eligible"],
    queryFn: () => purchaseOrdersApi.list({ status: "Approved", page: 1, pageSize: 50 }),
  });

  // PO detail when one is selected
  const poDetail = useQuery({
    queryKey: ["purchase-order", poId],
    queryFn: () => purchaseOrdersApi.get(poId),
    enabled: !!poId,
  });

  // When poDetail loads, build draft receipt rows from pending lines
  useEffect(() => {
    if (poDetail.data && poId) {
      const d = poDetail.data as {
        warehouseId: string;
        lines: { id: string; itemId: string; itemName: string; sku: string; qty: number; receivedQty: number; rate: number }[];
      };
      setWarehouseId(d.warehouseId);
      setLines(d.lines
        .filter(l => l.qty - l.receivedQty > 0)
        .map(l => ({
          purchaseOrderLineId: l.id,
          itemId: l.itemId, itemName: l.itemName, sku: l.sku,
          orderedQty: l.qty, alreadyReceived: l.receivedQty, pending: l.qty - l.receivedQty,
          poRate: l.rate,
          batchNo: "", expiryDate: "",
          receivedQty: l.qty - l.receivedQty,
          acceptedQty: l.qty - l.receivedQty,
          rejectedQty: 0,
          unitCost: l.rate,
          // Not defaulted from anything. The MRP is printed on the pack in the buyer's hand and on
          // the vendor's invoice; guessing it from the item master would put a stale figure on a
          // new batch and defeat the point of recording it per batch.
          mrp: 0,
          remarks: "",
        })));
    }
  }, [poDetail.data, poId]);

  const updateLine = (idx: number, patch: Partial<DraftReceipt>) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const validate = (): string | null => {
    if (!poId) return "Select a purchase order";
    if (!warehouseId) return "Warehouse must be set";
    if (lines.length === 0) return "No pending lines on this PO";
    for (const [i, l] of lines.entries()) {
      if (l.receivedQty <= 0) continue; // a zero-receipt row is filtered before submission
      if (!l.batchNo) return `Line ${i + 1}: batch number required`;
      if (Math.abs(l.acceptedQty + l.rejectedQty - l.receivedQty) > 0.0001)
        return `Line ${i + 1}: accepted + rejected must equal received`;
      if (l.receivedQty > l.pending + 0.0001)
        return `Line ${i + 1}: cannot receive more than pending (${l.pending})`;
      if (l.unitCost < 0) return `Line ${i + 1}: unit cost must be >= 0`;
      // Mirrors the server rule. Billing is capped at MRP, so an MRP under cost means the batch
      // could only ever be sold at a loss — almost always a keying slip.
      if (l.mrp > 0 && l.mrp < l.unitCost)
        return `Line ${i + 1}: MRP is below the unit cost`;
    }
    if (lines.every(l => l.receivedQty === 0)) return "Enter at least one received quantity";
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "GRN created" },
    mutationFn: async () => {
      const payload: CreateGrnRequest = {
        purchaseOrderId: poId,
        warehouseId,
        documentDate,
        deliveryNoteRef: deliveryNoteRef || undefined,
        notes: notes || undefined,
        lines: lines.filter(l => l.receivedQty > 0).map(l => ({
          purchaseOrderLineId: l.purchaseOrderLineId,
          itemId: l.itemId,
          batchNo: l.batchNo,
          expiryDate: l.expiryDate || undefined,
          receivedQty: l.receivedQty,
          acceptedQty: l.acceptedQty,
          rejectedQty: l.rejectedQty,
          unitCost: l.unitCost,
          // Blank means the invoice did not state one — send nothing rather than a zero, which
          // the server would read as an assertion that the pack is unpriced.
          mrp: l.mrp > 0 ? l.mrp : undefined,
          remarks: l.remarks || undefined,
        })),
      };
      const r = await grnApi.create(payload);
      if (postAfterCreate) await grnApi.post(r.id);
      return r;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grns"] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      navigate("/procurement/grns");
    },
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New goods receipt note</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Approved purchase order *"
              size="small" fullWidth value={poId} onChange={(e) => setPoId(e.target.value)}>
              <option value="">— Select PO —</option>
              {eligiblePoQuery.data?.items?.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.documentNo} — {po.vendorName} ({inr.format(po.totalAmount)})
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Warehouse *"
              size="small" fullWidth value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
              disabled={!!poDetail.data}>
              <option value="">— Pick branch —</option>
              {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Document date *" type="date" size="small" fullWidth
              value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Delivery note ref" size="small" fullWidth
              value={deliveryNoteRef} onChange={(e) => setDeliveryNoteRef(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Notes" size="small" fullWidth multiline rows={1}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {poDetail.isLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}

        {lines.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Receipt lines</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <b>Enter the MRP printed on the pack.</b> It is what the batch bills at and the ceiling
              a sale may not exceed — leave it blank and the batch stays unpriced, falling back to
              the item&apos;s default MRP for every sale of it. Correct it later under{" "}
              <b>Inventory → Stock</b> if the invoice is unclear now.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Ordered</TableCell>
                  <TableCell align="right">Pending</TableCell>
                  <TableCell sx={{ width: 130 }}>Batch # *</TableCell>
                  <TableCell sx={{ width: 150 }}>Expiry</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Received</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Accepted</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Rejected</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Unit cost</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>MRP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, idx) => (
                  <TableRow key={l.purchaseOrderLineId}>
                    <TableCell>
                      <Stack>
                        <strong>{l.sku}</strong>
                        <span>{l.itemName}</span>
                        {/* Putaway: tell the receiver where this item lives, so stock lands where
                            the system thinks it is. Also how a wrong bin gets noticed — the person
                            reading it is standing in front of the shelf. */}
                        <BinHint itemId={l.itemId} warehouseId={warehouseId} label="Put away in" />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{l.orderedQty}</TableCell>
                    <TableCell align="right"><Chip size="small" label={l.pending} /></TableCell>
                    <TableCell>
                      <TextField size="small" value={l.batchNo}
                        onChange={(e) => updateLine(idx, { batchNo: e.target.value })}
                        error={!l.batchNo && l.receivedQty > 0}
                        inputProps={{ maxLength: 50 }} sx={{ width: 120 }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="date" value={l.expiryDate}
                        onChange={(e) => updateLine(idx, { expiryDate: e.target.value })}
                        InputLabelProps={{ shrink: true }} sx={{ width: 140 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.receivedQty}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateLine(idx, { receivedQty: v, acceptedQty: v - l.rejectedQty });
                        }}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                        sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.acceptedQty}
                        onChange={(e) => updateLine(idx, { acceptedQty: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                        sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.rejectedQty}
                        onChange={(e) => {
                          const v = Number(e.target.value) || 0;
                          updateLine(idx, { rejectedQty: v, acceptedQty: l.receivedQty - v });
                        }}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                        sx={{ width: 90 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.unitCost}
                        onChange={(e) => updateLine(idx, { unitCost: Number(e.target.value) || 0 })}
                        inputProps={{ min: 0, step: "0.0001", style: { textAlign: "right" } }}
                        sx={{ width: 110 }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={l.mrp || ""}
                        placeholder="—"
                        onChange={(e) => updateLine(idx, { mrp: Number(e.target.value) || 0 })}
                        error={l.mrp > 0 && l.mrp < l.unitCost}
                        helperText={l.mrp > 0 && l.mrp < l.unitCost ? "below cost" : undefined}
                        inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                        sx={{ width: 110 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={2} alignItems="center">
          <Button onClick={() => navigate("/procurement/grns")}>Cancel</Button>
          <Button variant="outlined"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending && !postAfterCreate ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => { setPostAfterCreate(false); mutation.mutate(); }}>
            Save draft
          </Button>
          <Button variant="contained" color="success"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending && postAfterCreate ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => { setPostAfterCreate(true); mutation.mutate(); }}>
            Receive & post (updates stock)
          </Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}

